// Step 2: Schedule & Logistics - departure, duration, activity level, meeting point, rental boots
// Required for published tours; optional for concept tours

import React from 'react';
import { Clock, MapPin, Activity, Users } from 'lucide-react';
import { Tour, ActivityLevel } from '../../../types/tours';

interface StepScheduleProps {
  formData: Partial<Tour>;
  isConcept: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onCheckboxChange: (field: string, checked: boolean) => void;
}

const activityLevels: ActivityLevel[] = ['relaxed', 'easy', 'moderate', 'challenging', 'extreme'];

const StepSchedule: React.FC<StepScheduleProps> = ({ formData, isConcept, onChange, onCheckboxChange }) => {
  const req = !isConcept;

  return (
    <div className="space-y-6">
      <div className="bg-[#1F2B1F] rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-6">Schedule & Logistics</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-white/70 mb-2">
              Location {req && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location || ''}
                onChange={onChange}
                placeholder="e.g., Drake Bay, Osa Peninsula"
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/30 transition-all"
              />
            </div>
          </div>

          <div>
            <label htmlFor="departure" className="block text-sm font-medium text-white/70 mb-2">
              Departure Time {req && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="time"
                id="departure"
                name="departure"
                value={formData.departure || ''}
                onChange={onChange}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white transition-all"
              />
            </div>
          </div>

          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-white/70 mb-2">
              Duration {req && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              id="duration"
              name="duration"
              value={formData.duration || ''}
              onChange={onChange}
              placeholder='e.g., 3 hours'
              className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/30 transition-all"
            />
            <p className="text-xs text-white/40 mt-1">Enter duration (e.g., "3 hours" or "2.5 hours")</p>
          </div>

          <div>
            <label htmlFor="activity_level" className="block text-sm font-medium text-white/70 mb-2">
              Activity Level {req && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <Activity className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <select
                id="activity_level"
                name="activity_level"
                value={formData.activity_level || ''}
                onChange={onChange}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white appearance-none transition-all"
              >
                <option value="" className="bg-[#1F2B1F]">Select activity level</option>
                {activityLevels.map(level => (
                  <option key={level} value={level} className="bg-[#1F2B1F]">
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="min_persons" className="block text-sm font-medium text-white/70 mb-2">
              Minimum Persons Required
            </label>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="number"
                id="min_persons"
                name="min_persons"
                value={formData.min_persons ?? 1}
                onChange={onChange}
                min="1"
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white transition-all"
              />
            </div>
            <p className="text-xs text-white/40 mt-1">Minimum number of persons required to book this tour</p>
          </div>

          <div className="flex items-center">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.has_rental_boots || false}
                onChange={(e) => onCheckboxChange('has_rental_boots', e.target.checked)}
                className="w-5 h-5 text-[#E85C0D] bg-white/5 border-white/20 rounded focus:ring-[#E85C0D]"
              />
              <div>
                <span className="text-white">Offer rental rubber boots</span>
                <p className="text-xs text-white/40 mt-0.5">Collect boot sizes from participants</p>
              </div>
            </label>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="meeting_point" className="block text-sm font-medium text-white/70 mb-2">
              Meeting Point {req && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="text"
                id="meeting_point"
                name="meeting_point"
                value={formData.meeting_point || ''}
                onChange={onChange}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/30 transition-all"
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="meeting_point_url" className="block text-sm font-medium text-white/70 mb-2">
              Meeting Point Google Maps URL {req && <span className="text-red-400">*</span>}
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="url"
                id="meeting_point_url"
                name="meeting_point_url"
                value={formData.meeting_point_url || ''}
                onChange={onChange}
                placeholder="https://maps.google.com/..."
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/30 transition-all"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepSchedule;
